import type { JobStatus, JobCardStatus, JobInvoiceStatus } from '@jobos/shared/types/job';
import type { JobPhase } from '../components/JobPhaseSelector';

export interface JobFilters {
  status?: JobStatus[];
  jobCardStatus?: JobCardStatus[];
  invoiceStatus?: JobInvoiceStatus[];
  projectType?: string[];
  clientId?: string[];
  search?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  isArchived?: boolean | 'only';
  showDeleted?: 'only';
  dateField?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface JobListQueryState {
  phase: JobPhase;
  statuses: string[];
  jobCardStatuses: string[];
  invoiceStatuses: string[];
  projectTypes: string[];
  clientIds: string[];
  search: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  archived: boolean | 'only';
  showDeleted: boolean;
  dateField?: string;
  dateFrom?: string;
  dateTo?: string;
}

const VALID_PHASES: JobPhase[] = ['all', 'quoting', 'building', 'billing', 'other'];

export const normalizeQuery = (query: string) => {
  const raw = query.startsWith('?') ? query.slice(1) : query;
  const params = new URLSearchParams(raw);
  params.delete('default');
  const entries = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));
  const normalized = new URLSearchParams(entries).toString();
  return normalized ? `?${normalized}` : '';
};


export const buildQueryFromState = ({
  phase,
  statuses,
  jobCardStatuses,
  invoiceStatuses,
  projectTypes,
  clientIds,
  archived,
  sortField,
  sortDirection,
  search,
  showDeleted,
  dateField,
  dateFrom,
  dateTo,
}: JobListQueryState) => {
  const params = new URLSearchParams();
  if (phase !== 'all') {
    params.set('phase', phase);
  }
  if (statuses.length > 0) {
    params.set('status', statuses.join(','));
  }
  if (jobCardStatuses.length > 0) {
    for (const value of jobCardStatuses) params.append('jobCardStatus', value);
  }
  if (invoiceStatuses.length > 0) {
    for (const value of invoiceStatuses) params.append('invoiceStatus', value);
  }
  if (projectTypes.length > 0) {
    params.set('projectType', projectTypes.join(','));
  }
  if (clientIds.length > 0) {
    params.set('clientId', clientIds.join(','));
  }
  if (archived !== false) {
    params.set('archived', archived === 'only' ? 'only' : archived ? 'true' : 'false');
  }
  if (showDeleted) {
    params.set('showDeleted', 'only');
  }
  if (sortField) {
    params.set('sortField', sortField);
  }
  if (sortDirection) {
    params.set('sortDirection', sortDirection);
  }
  if (search.trim()) {
    params.set('search', search.trim());
  }
  if (dateField && dateField !== 'statusDate') {
    params.set('dateField', dateField);
  }
  if (dateFrom) {
    params.set('dateFrom', dateFrom);
  }
  if (dateTo) {
    params.set('dateTo', dateTo);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const parseJobListQuery = (search: string): JobListQueryState => {
  const params = new URLSearchParams(search);
  params.delete('default');
  // Legacy billingStatus URL key is intentionally ignored — kept here as a
  // no-op pass-through so stale URLs don't error after the 2026-05-04 split.
  params.delete('billingStatus');

  const phaseParam = params.get('phase');
  const phase = phaseParam && VALID_PHASES.includes(phaseParam as JobPhase)
    ? (phaseParam as JobPhase)
    : 'all';
  const statuses = params.get('status')?.split(',').filter(Boolean) || [];
  const jobCardStatuses = params.getAll('jobCardStatus').filter(Boolean);
  const invoiceStatuses = params.getAll('invoiceStatus').filter(Boolean);
  const projectTypes = params.get('projectType')?.split(',').filter(Boolean) || [];
  const clientIds = params.get('clientId')?.split(',').filter(Boolean) || [];
  const searchText = params.get('search') || '';
  const sortField = params.get('sortField') || undefined;
  const sortDirection =
    params.get('sortDirection') === 'asc' || params.get('sortDirection') === 'desc'
      ? (params.get('sortDirection') as 'asc' | 'desc')
      : undefined;
  const archivedParam = params.get('archived');
  const archived =
    archivedParam === 'only' ? 'only' : archivedParam === 'true' ? true : false;
  const showDeletedParam = params.get('showDeleted');
  const showDeleted = showDeletedParam === 'only';

  const VALID_DATE_FIELDS = ['statusDate', 'noticeDate', 'estimateSendDate', 'startDate', 'completedDate', 'lastInvoicedDate', 'createdAt'];
  const dateFieldParam = params.get('dateField');
  const dateField = dateFieldParam && VALID_DATE_FIELDS.includes(dateFieldParam) ? dateFieldParam : undefined;
  const dateFrom = params.get('dateFrom') || undefined;
  const dateTo = params.get('dateTo') || undefined;

  return {
    phase,
    statuses,
    jobCardStatuses,
    invoiceStatuses,
    projectTypes,
    clientIds,
    search: searchText,
    sortField,
    sortDirection,
    archived,
    showDeleted,
    dateField,
    dateFrom,
    dateTo,
  };
};
